// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@store/MobiusOverrides/Button/Button";
import {
    getEligibleCampaigns,
    markCampaignAsRead,
    markCampaignAsShownInSession,
    markCampaignAsUnread,
} from "@store/Framework/Services/FlyoutCampaignService";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    campaigns = "campaigns",
}

export interface CampaignItem {
    fields: {
        campaignId: string;
        campaignTitle: string;
        startDate: string;
        endDate: string;
        richContent: string;
        isActive: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.campaigns]: CampaignItem[];
    };
}

interface FlyoutCampaignNotificationStyles {
    modalContent?: InjectableCss;
    buttonContainer?: InjectableCss;
    campaignContent?: InjectableCss;
}

const flyoutCampaignNotificationStyles: FlyoutCampaignNotificationStyles = {
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: ${pxToRem(10)};
            margin-top: ${pxToRem(20)};
        `,
    },
    campaignContent: {
        css: css`
            margin-bottom: ${pxToRem(20)};

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                color: ${getColor("text.main")};
                margin-top: 0;
                margin-bottom: ${pxToRem(12)};
            }

            p {
                margin-bottom: ${pxToRem(16)};
                line-height: 1.6;
            }

            ul,
            ol {
                margin-bottom: ${pxToRem(16)};
                padding-left: ${pxToRem(20)};
            }

            li {
                margin-bottom: ${pxToRem(8)};
            }

            a {
                ${linkStyles("primary", "primary.light", false, true)}
            }

            img {
                max-width: 100%;
                height: auto;
                border-radius: ${pxToRem(8)};
                margin: ${pxToRem(16, 0)};
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentUserId: state.context?.session.userProfileId,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const styles = flyoutCampaignNotificationStyles;

const FlyoutCampaignNotification: React.FunctionComponent<Props> = ({ fields: { campaigns }, currentUserId }) => {
    const [currentCampaign, setCurrentCampaign] = useState<CampaignItem | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load and filter eligible campaigns on component mount
    useEffect(() => {
        const loadEligibleCampaigns = async () => {
            if (!currentUserId || !campaigns?.length) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const eligibleCampaignData = await getEligibleCampaigns(campaigns, currentUserId);

                setCurrentCampaign(eligibleCampaignData);

                // Show the first eligible campaign immediately
                if (eligibleCampaignData) {
                    setIsVisible(true);
                }
            } catch (error) {
                setError("Failed to load campaigns");
            } finally {
                setIsLoading(false);
            }
        };

        loadEligibleCampaigns();
    }, [currentUserId, campaigns]);

    // Mark campaign as shown in session when it's displayed
    useEffect(() => {
        if (currentCampaign && isVisible) {
            markCampaignAsShownInSession();
            handleDismiss();
        }
    }, [currentCampaign, isVisible]);

    const handleDismiss = useCallback(async () => {
        if (!currentCampaign || !currentUserId) {
            setIsVisible(false);
            return;
        }

        setIsLoading(true);

        try {
            await markCampaignAsRead(currentUserId, currentCampaign.fields.campaignId);
        } catch (error) {
            setError("Failed to save campaign status");
        } finally {
            setIsLoading(false);
        }
    }, [currentCampaign, currentUserId]);

    const handleRemindMeLater = useCallback(async () => {
        if (!currentCampaign || !currentUserId) {
            setIsVisible(false);
            return;
        }
        setIsLoading(true);

        try {
            await markCampaignAsUnread(currentUserId, currentCampaign.fields.campaignId);
        } catch (error) {
            setError("Failed to save campaign status");
        } finally {
            setIsLoading(false);
            setIsVisible(false);
        }
    }, [currentCampaign, currentUserId]);

    if (!currentCampaign || !currentCampaign || error) {
        return null;
    }

    return (
        <StyledWrapper>
            <Modal
                isOpen={isVisible}
                headline={currentCampaign.fields.campaignTitle}
                handleClose={() => setIsVisible(false)}
                data-test-selector="flyoutCampaign_modal"
            >
                <StyledWrapper {...styles.modalContent}>
                    <StyledWrapper {...styles.campaignContent}>
                        {parse(currentCampaign.fields.richContent, parserOptions)}
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonContainer}>
                        <Button
                            variant="primary"
                            isLoading={isLoading}
                            onClick={handleRemindMeLater}
                            data-test-selector="flyoutCampaign_remindLaterButton"
                        >
                            {translate("Remind Me Later")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(FlyoutCampaignNotification)),
    definition: {
        displayName: "Flyout Campaign Notification",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.campaigns,
                displayName: "Campaigns",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) =>
                    (item.fields as any).campaignTitle || `Campaign ${(item.fields as any).campaignId}`,
                defaultValue: [],
                fieldType: "Contextual",
                tooltip: "Configure campaign notifications",
                fieldDefinitions: [
                    {
                        name: "campaignId",
                        displayName: "Campaign ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "campaignTitle",
                        displayName: "Campaign Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "richContent",
                        displayName: "Campaign Content",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "startDate",
                        displayName: "Start Date",
                        editorTemplate: "DateTimeField",
                        defaultValue: new Date(),
                        isRequired: true,
                    },
                    {
                        name: "endDate",
                        displayName: "End Date",
                        editorTemplate: "DateTimeField",
                        defaultValue: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                        isRequired: true,
                    },
                    {
                        name: "isActive",
                        displayName: "Active",
                        editorTemplate: "CheckboxField",
                        variant: "toggle",
                        defaultValue: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
